import { Endorsements } from './Endorsements.js';
import * as S from './Hero.styles.js';
import { type UserProfile_userProfile$key } from '@/__generated__/relay/UserProfile_userProfile.graphql.js';
import { Avatar } from '@/components/Avatar/Avatar.js';
import { InquiryButton } from '@/components/InquiryButton/InquiryButton.js';
import { Layout } from '@/components/Layout/Layout.js';
import { MobileLayout } from '@/components/MobileLayout/MobileLayout.js';
import { RecommendationStack } from '@/components/RecommendationStack/RecommendationStack.js';
import { Section } from '@/components/Section/Section.js';
import { TopIndependentBadge } from '@/components/TopIndependentBadge/TopIndependentBadge.js';
import { UserProfileFragment } from '@/documents/fragments/UserProfile.js';
import { useHeadlineVariant } from '@/hooks/useHeadlineVariant.js';
import { useIPFragment } from '@/hooks/useIPFragment.js';
import { useMatchMedia } from '@/hooks/useMatchMedia.js';
import { minWidthMediaQueries } from '@/stitches/media.js';
import { buildHeadline } from '@/templates/sydney/utilities/buildHeadline.js';
import { buildUserRoles } from '@/utilities/buildUserRoles.js';
import { useLayoutEffect, useRef, useState } from 'react';

export type HeroProps = {
  readonly queryRef: UserProfile_userProfile$key;
};

const isInView = (element: HTMLDivElement | null) => {
  if (!element) return true;
  const box = element.getBoundingClientRect();
  return box.top < window.innerHeight && box.bottom >= 0;
};

export const Hero = ({ queryRef }: HeroProps) => {
  const userProfile = useIPFragment<UserProfile_userProfile$key>(
    UserProfileFragment,
    queryRef,
  );

  const isMediumViewport = useMatchMedia(minWidthMediaQueries.bp2);
  const afterSocialLinksRef = useRef<HTMLDivElement>(null);
  const [scaleTextDown, setScaleTextDown] = useState(false);
  const headlineFontFamily = useHeadlineVariant();

  useLayoutEffect(() => {
    const inView = isInView(afterSocialLinksRef.current);
    if (!inView) setScaleTextDown(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [afterSocialLinksRef.current]);

  if (!userProfile) return null;

  const avatar = userProfile.avatarImage ?? null;
  const firstName = userProfile.firstName ?? '';
  const lastName = userProfile.lastName ?? '';
  const fullName = `${firstName} ${lastName}`;
  const size = isMediumViewport ? 300 : 180;
  const roles = buildUserRoles(userProfile?.roles?.edges ?? []);
  const headline = buildHeadline(fullName, roles);

  if (!isMediumViewport) {
    return (
      <MobileLayout>
        <MobileLayout.Avatar uid={userProfile.avatarImage?.uid ?? ''} />
        <MobileLayout.Heading>{headline ?? ''}</MobileLayout.Heading>
        <MobileLayout.InquiryButton />
        <S.MobileBadgeContainer>
          <TopIndependentBadge
            css={{
              margin: '0',
            }}
          />
          <RecommendationStack />
        </S.MobileBadgeContainer>
        <MobileLayout.Socials />
      </MobileLayout>
    );
  }

  return (
    <Section margin="b">
      <S.HeroGrid>
        <Layout.GridColumn
          initial={11}
          sm={9}
          xl={6}
        >
          {avatar ? (
            <S.AvatarContainer css={{ position: 'relative', zIndex: -1 }}>
              <Avatar
                height={size}
                rounded
                uid={avatar.uid}
                width={size}
              />
            </S.AvatarContainer>
          ) : null}
        </Layout.GridColumn>
        <Layout.GridColumn
          initial={12}
          lg={11}
        >
          <Layout.FlexRow css={{ alignItems: 'center', gap: '$16' }}>
            <TopIndependentBadge
              css={{
                margin: '0',
              }}
            />
            <Endorsements />
          </Layout.FlexRow>
          <S.Headline
            as="h1"
            fontFamily={headlineFontFamily}
            key={scaleTextDown.toString()}
            scaleTextDown={scaleTextDown}
          >
            {headline}
          </S.Headline>
        </Layout.GridColumn>
        <Layout.GridColumn
          initial={12}
          lg={9}
        >
          <S.ButtonContainer>
            <InquiryButton
              css={{ textTransform: 'uppercase' }}
              section="cta_profile"
              solidBg
            >
              Work With Me
            </InquiryButton>
          </S.ButtonContainer>
          <div
            ref={afterSocialLinksRef}
            style={{ height: '$4', width: '100%' }}
          />
        </Layout.GridColumn>
      </S.HeroGrid>
    </Section>
  );
};
